import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const ITEM_SEPARATOR = ';';
const KEY_VALUE_SEPARATOR = '=';
const VIEWS_SEPARATOR = '/';

function hashToMap<T extends string = string>(hash: string): Partial<Record<string, T>> {
	return hash
		.replace('#', '')
		.split(ITEM_SEPARATOR)
		.filter(Boolean)
		.reduce((acc, cur) => {
			const [key, value] = cur.split(KEY_VALUE_SEPARATOR);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			acc[key!] = value as T;
			return acc;
		}, {} as Record<string, T>);
}

function mapToHash<T extends string = string>(map: Partial<Record<string, T>>): string {
	return Object.entries(map)
		.map((entry) => entry.join(KEY_VALUE_SEPARATOR))
		.join(ITEM_SEPARATOR);
}

function areViewsEqual(viewDefinition: string, currentView: string, exact: boolean) {
	const regExpContent = viewDefinition
		.split('/')
		.map((item) => (item.startsWith(':') ? '[^/]+' : item))
		.join('/');
	const regExp = new RegExp(`^${regExpContent}${exact ? '$' : ''}`);
	return regExp.test(currentView);
}

function fillWithParams(view: string, params: Record<string, string | number>) {
	const parts = view.split('/');
	const requiredParams = parts.filter((part) => part.startsWith(':')).map((item) => item.substring(1));

	if (!requiredParams.every((requiredParam) => params.hasOwnProperty(requiredParam))) {
		throw new Error('You need to provide all parameters to the parametrized path');
	}

	return parts.map((item) => (item.startsWith(':') ? params[item.substring(1)] : item)).join('/');
}

export default function useLocalNavigation<T extends string = string>(namespace: string, defaultView: T) {
	const history = useHistory();
	const location = useLocation();

	const updateHash = useCallback(
		(hash: string) => {
			if (hash !== location.hash) {
				history.push({ hash });
			}
		},
		[history, location],
	);

	const getHash = useCallback(() => location.hash, [location]);

	const getView = useCallback(() => {
		const map = hashToMap<T>(getHash());
		return map[namespace] || defaultView;
	}, [getHash, namespace, defaultView]);

	const getViewParams = useCallback(
		(viewDefinition: string) => {
			const definitionParts = viewDefinition.split('/');
			const currentParts = getView().split('/');

			return definitionParts.reduce((acc, cur, index) => {
				if (cur.startsWith(':')) {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					acc[cur.substring(1)] = currentParts[index]!;
				}
				return acc;
			}, {} as Record<string, string>);
		},
		[getView],
	);

	const findViewDefinition = useCallback(
		(viewDefinitions: Record<any, string>) => {
			return Object.values(viewDefinitions).find((view) => areViewsEqual(view, getView(), true));
		},
		[getView],
	);

	const generateViewHash = useCallback(
		(viewName: T, params: Record<string, any> = {}) => {
			const map = hashToMap<T>(getHash());
			map[namespace] = fillWithParams(viewName, params) as T;
			return mapToHash(map);
		},
		[getHash, namespace],
	);

	const setView = useCallback(
		(viewName: T, params: Record<string, any> = {}) => {
			if (!viewName) {
				return;
			}
			updateHash(generateViewHash(viewName, params));
		},
		[updateHash, generateViewHash],
	);

	const goToRootView = useCallback(() => {
		const map = hashToMap<T>(getHash());
		delete map[namespace];
		updateHash(mapToHash<T>(map));
	}, [getHash, updateHash, namespace]);

	const goToParentView = useCallback(() => {
		const currentView = getView();
		const parts = currentView.split(VIEWS_SEPARATOR);
		if (parts.length > 1) {
			setView(parts.slice(0, -1).join(VIEWS_SEPARATOR) as T);
		} else {
			goToRootView();
		}
	}, [getView, setView, goToRootView]);

	const isViewActive = useCallback((view: T, exact = false) => areViewsEqual(view, getView(), exact), [getView]);

	const isChildViewActive = useCallback(
		(parentView: T) => !areViewsEqual(parentView, getView(), true) && areViewsEqual(parentView, getView(), false),
		[getView],
	);

	return {
		setView,
		goToRootView,
		goToParentView,
		isViewActive,
		isChildViewActive,
		getViewParams,
		findViewDefinition,
		generateViewHash,
	};
}
